import { ChevronDown } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Carousel from '@/components/shared/Carousel';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import Provider from '@/features/ProductSelection/components/cards/Provider';
import Quote from '@/features/ProductSelection/components/cards/Quote';
import { cn } from '@/lib/utils';
import { SupportedProductType } from '@/types/api';
import type { AppData } from '@/types/app';
import type { RequestQuoteGroup, UseRequestQuotesResult } from '../hooks/useRequestQuotes';
import CoverageDetails from './CoverageDetails';
import SectionCard from './SectionCard';

interface QuotesOverviewProps {
	/** Lifted to the page so the package overview can share one fetch. */
	quotes: UseRequestQuotesResult;
	appData: AppData;
}

const noop = (): void => {};

/** Renders one group's quotes read-only, reusing the flow's own quote cards. */
const GroupCards = ({ group }: { group: RequestQuoteGroup }): React.JSX.Element => {
	const { t } = useTranslation();

	const cards =
		group.kind === 'ohs'
			? group.ohsQuotes.map((provider) => (
					<Provider
						key={provider.quote_id}
						provider={provider}
						isSelected={provider.quote_id === group.selectedQuoteId}
						isDisabled={false}
						isTransitioning={false}
						onSelect={noop}
						readOnly
					/>
				))
			: group.insuranceQuotes.map((quote) => (
					<Quote
						key={quote.quote_id}
						quote={quote}
						productType={group.productType}
						isSelected={quote.quote_id === group.selectedQuoteId}
						isDisabled={false}
						isTransitioning={false}
						onSelect={noop}
						readOnly
						showPremiumRate
					/>
				));

	if (cards.length === 0) {
		return (
			<p className="text-sm italic text-muted-foreground/70">
				{t('requestOverview.quotes.noQuotes')}
			</p>
		);
	}

	return (
		<Carousel testId={`dossier-quote-carousel-${group.key}`} flushSlides slideGapPx={20}>
			{cards}
		</Carousel>
	);
};

/**
 * "Quotes per product_type": one section per relevant product type, each a
 * carousel of the flow's own quote cards (read-only) with the customer's
 * selected quote highlighted. The short-term sick-leave section exposes a
 * "Dekkingsgegevens" toggle that reveals the coverage details.
 */
const QuotesOverview = ({ quotes, appData }: QuotesOverviewProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { groups, isLoading, error, refetch } = quotes;
	const [showCoverage, setShowCoverage] = useState(false);

	if (isLoading) {
		return (
			<SectionCard
				title={t('requestOverview.quotes.title')}
				testId="dossier-section-quotes"
				bordered={false}
			>
				<div className="flex justify-center py-10">
					<Spinner size="lg" visible />
				</div>
			</SectionCard>
		);
	}

	if (error) {
		return (
			<SectionCard
				title={t('requestOverview.quotes.title')}
				testId="dossier-section-quotes"
				bordered={false}
			>
				<p className="mb-3 text-sm text-destructive">{error}</p>
				<Button type="button" variant="outline" size="sm" onClick={refetch}>
					{t('common.retry')}
				</Button>
			</SectionCard>
		);
	}

	return (
		<div className="flex flex-col gap-14" data-testid="dossier-section-quotes">
			{groups.map((group) => {
				const isSickLeave = group.key === SupportedProductType.SICK_LEAVE_SHORT_TERM;
				const coverageToggle = isSickLeave ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => setShowCoverage((open) => !open)}
						iconRight={
							<ChevronDown
								className={cn('h-4 w-4 transition-transform', showCoverage && 'rotate-180')}
							/>
						}
						aria-expanded={showCoverage}
						data-testid="dossier-coverage-toggle"
					>
						{t('requestOverview.quotes.coverageDetails')}
					</Button>
				) : undefined;

				return (
					<SectionCard
						key={group.key}
						title={group.label}
						action={coverageToggle}
						testId={`dossier-quote-group-${group.key}`}
						bordered={false}
					>
						{isSickLeave && showCoverage && (
							<div className="mb-5 border-b border-gray-200 pb-5">
								<CoverageDetails appData={appData} />
							</div>
						)}
						<GroupCards group={group} />
					</SectionCard>
				);
			})}
		</div>
	);
};

export default QuotesOverview;
